// SpreadsheetProcessorService.js
// Service for processing CSV/XLSX spreadsheet data for product imports

class SpreadsheetProcessorService {
  constructor() {
    // Valid product types for validation
    this.validProductTypes = [
      'digital',
      'physical',
      'aluguel',
      'assinatura',
      'consulta',
      'delivery',
      'tratamento'
    ];

    // Product type labels for display
    this.productTypeLabels = {
      'digital': 'Produtos digitais',
      'physical': 'Produtos físicos',
      'aluguel': 'Serviços do tipo aluguel',
      'assinatura': 'Assinaturas',
      'consulta': 'Consultas',
      'delivery': 'Serviços do tipo delivery',
      'tratamento': 'Tratamentos'
    };
  }

  // Normalize headers to lowercase and create a case-insensitive mapping
  normalizeHeaders(headers) {
    const normalized = {};
    headers.forEach(header => {
      if (header) {
        const key = header.toString().trim().toLowerCase();
        normalized[key] = header;
      }
    });
    return normalized;
  }

  // Normalize boolean values from various formats
  normalizeBooleanValue(value) {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    
    const strValue = value.toString().toLowerCase().trim();
    return strValue === 'true' || strValue === '1' || strValue === 'yes' || strValue === 'sim';
  }

  // Convert cell value handling booleans from Google Sheets
  normalizeCellValue(cellValue) {
    // Handle boolean values from Google Sheets
    if (cellValue === true || cellValue === 'TRUE' || cellValue === 'True') {
      return 'true';
    } else if (cellValue === false || cellValue === 'FALSE' || cellValue === 'False') {
      return 'false';
    } else {
      return (cellValue || '').toString().trim();
    }
  }

  // Get field value with case-insensitive fallback
  getFieldValue(row, ...fieldNames) {
    for (const fieldName of fieldNames) {
      if (row[fieldName] !== undefined && row[fieldName] !== null && row[fieldName] !== '') {
        return row[fieldName];
      }
      // Try lowercase version
      const lowercase = fieldName.toLowerCase();
      if (row[lowercase] !== undefined && row[lowercase] !== null && row[lowercase] !== '') {
        return row[lowercase];
      }
    }
    return null;
  }

  // Clean description field
  cleanDescription(raw) {
    if (!raw) return '';
    // 1) replace literal "\n" with real newlines
    let text = raw.replace(/\\n/g, '\n').trim();

    // 2) remove any HTML tags (e.g. <img>, <div>, etc.)
    text = text.replace(/<[^>]+>/g, '');

    // 3) truncate to 2048 characters to prevent database limit issues
    return text.substring(0, 2048);
  }

  // Format price values handling different formats
  formatPrice(price) {
    if (!price) return null;
    
    const priceStr = price.toString();
    
    // Convert to string and remove any non-numeric characters except period and comma
    let formatted = priceStr.trim().replace(/[^\d,.]/g, '');
    
    // Handle different number formats
    if (formatted.includes(',') && formatted.includes('.')) {
      // Format like "1.259,90" - European format with thousands separator
      // Last comma is decimal separator, everything before is thousands
      const lastCommaIndex = formatted.lastIndexOf(',');
      const beforeDecimal = formatted.substring(0, lastCommaIndex).replace(/[,.]/g, '');
      const afterDecimal = formatted.substring(lastCommaIndex + 1);
      formatted = beforeDecimal + '.' + afterDecimal;
    } else if (formatted.includes(',')) {
      // Format like "59,9" - European decimal format
      // Replace comma with period for decimal
      formatted = formatted.replace(/,/g, '.');
    }
    // If only periods, assume it's already in correct format (like "59.9" or "1259.90")
    
    const floatValue = parseFloat(formatted);
    return isNaN(floatValue) ? null : floatValue;
  }

  // Validate image URLs to reject base64 format
  validateImageUrls(imageUrls) {
    if (!imageUrls || imageUrls.length === 0) return imageUrls;

    // Check each URL for base64 format
    for (const url of imageUrls) {
      if (url && url.trim().startsWith('data:')) {
        throw new Error('Formato de imagem inválido encontrado. Por favor, utilize links de imagens válidas.');
      }
    }

    return imageUrls;
  }

  // Format array fields (categories, images, etc.)
  formatArrayField(value, shouldTruncate = true, validateImages = false) {
    if (!value) return [];

    // If already an array, return it
    if (Array.isArray(value)) return value;

    // Split by comma and trim each value
    let result;
    if (shouldTruncate) {
      // Truncate to 32 characters for non-image fields
      result = value.split(',').map(item => item.trim().substring(0, 32)).filter(Boolean);
    } else {
      // Don't truncate for image URLs
      result = value.split(',').map(item => item.trim()).filter(Boolean);
    }

    // Validate images if requested
    if (validateImages) {
      return this.validateImageUrls(result);
    }

    return result;
  }

  // Check if column header is valid
  isValidColumnHeader(header) {
    // Remove empty strings
    if (!header || header.trim() === '') return false;
    
    // Remove auto-generated headers like '_1', '_2', etc.
    if (/^_\d+$/.test(header.trim())) return false;
    
    return true;
  }

  // Filter columns to remove invalid headers
  filterValidColumns(headers, rows) {
    const validHeaders = headers.filter(header => this.isValidColumnHeader(header));
    
    // Filter rows to only include valid columns
    const filteredRows = rows.map(row => {
      const filteredRow = {};
      validHeaders.forEach(header => {
        filteredRow[header] = row[header];
      });
      return filteredRow;
    });
    
    return { headers: validHeaders, rows: filteredRows };
  }

  // Validate product types in data
  validateProductTypes(csvData) {
    const errors = [];
    
    csvData.forEach((row, index) => {
      if (row.product_type) {
        // Convert to string and normalize (handle booleans, numbers, etc.)
        const productType = row.product_type.toString().toLowerCase().trim();
        // Skip empty values and check only non-empty product types
        if (productType && !this.validProductTypes.includes(productType)) {
          errors.push({
            row: index + 1, // 1-based row number for user display
            value: row.product_type,
            validTypes: this.validProductTypes
          });
        }
      }
    });
    
    return errors;
  }

  // Auto-detect registration type based on unique fields
  detectRegistrationType(row) {
    const headers = Object.keys(row).map(h => h.toLowerCase());
    
    // First check if there's a product_type field and use it
    if (headers.includes('product_type') && row.product_type) {
      const productType = row.product_type.toString().toLowerCase().trim();
      // Only return valid product types, otherwise continue with detection
      if (this.validProductTypes.includes(productType)) {
        return productType;
      }
    }
    
    // Check for unique field combinations
    if (headers.includes('fileformat') && headers.includes('licensetype')) {
      return 'digital';
    } else if (headers.includes('weightkg') || headers.includes('color') || headers.includes('shipsto')) {
      return 'physical';
    } else if (headers.includes('maxdistance') && headers.includes('isfixedprice') && headers.includes('estimatedminutes')) {
      return 'delivery';
    } else if (headers.includes('plantype')) {
      return 'assinatura';
    } else if (headers.includes('specialty') && headers.includes('durationminutes')) {
      // More specific check for consulta/tratamento
      return 'consulta'; // Default to consulta if specialty field exists
    } else if (headers.includes('ishourlyrate') && headers.includes('advancebookingdays') && !headers.includes('specialty')) {
      return 'aluguel';
    } else {
      // Default fallback
      return 'digital';
    }
  }

  // Check if row has required fields
  isValidRow(row) {
    // Check required fields first (name and description)
    if (!row.name || !row.name.toString().trim()) return false;
    if (!row.description || !row.description.toString().trim()) return false;
    return true;
  }

  // Add shared booking fields to product
  addBookingFields(row, product) {
    // Check both camelCase and lowercase versions (headers are lowercased during parsing)
    const hourlyRateValue = this.getFieldValue(row, 'isHourlyRate', 'ishourlyrate');
    if (hourlyRateValue) {
      product.isHourlyRate = this.normalizeBooleanValue(hourlyRateValue);
    }
    
    const advanceBookingValue = this.getFieldValue(row, 'advanceBookingDays', 'advancebookingdays');
    if (advanceBookingValue) {
      product.advanceBookingDays = parseInt(advanceBookingValue) || 0;
    }
    
    if (row.address) product.address = row.address;
    if (row.link) product.contextUrl = row.link;
  }

  // Process a single product row
  processProductRow(row, productType, selectedInboxes) {
    const product = {};
    
    // Set product type
    product.product_type = productType;
    
    // Add selectedInboxes based on selection (for backend compatibility)
    if (selectedInboxes.includes('nenhum')) {
      product.selectedInboxes = ['disabled']; // This will result in inbox_id: null
    } else {
      product.selectedInboxes = selectedInboxes.includes('todos') ? ['todos'] : selectedInboxes;
    }
    
    // Required fields - use direct field names from CSV (validation already done by isValidRow)
    product.name = row.name.trim();
    product.description = this.cleanDescription(row.description);
    
    // Price field - default to 0 if empty
    // Check for price in different possible columns
    const priceValue = this.getFieldValue(row, 'price', 'preço', 'valor', 'Price', 'PRICE');
    const price = this.formatPrice(priceValue);
    product.price = price !== null ? price : 0;
    
    // Check both camelCase and lowercase versions
    const discountPriceValue = this.getFieldValue(row, 'discountPrice', 'discountprice');
    if (discountPriceValue) {
      const discountPrice = this.formatPrice(discountPriceValue);
      product.discountPrice = discountPrice !== null ? discountPrice : null;
    } else {
      product.discountPrice = null;
    }
    
    const discountReasonValue = this.getFieldValue(row, 'discountReason', 'discountreason');
    if (discountReasonValue) {
      product.discountReason = discountReasonValue;
    }
    
    // Array fields - handle multiple possible header variations as fallback
    const categoryValue = this.getFieldValue(row, 'category', 'categories', 'categoria', 'categorias', 'cat', 'tipo', 'tag', 'tags');
    product.category = this.formatArrayField(categoryValue);
    
    const imagesValue = this.getFieldValue(row, 'images', 'image', 'image_urls', 'image urls', 'imagens', 'foto', 'fotos', 'picture', 'pictures');
    product.images = this.formatArrayField(imagesValue, false, true); // Don't truncate, validate images
    
    // Add type-specific fields based on product type
    switch (productType) {
      case 'digital':
        this.addDigitalFields(row, product);
        break;
      case 'physical':
        this.addPhysicalFields(row, product);
        break;
      case 'delivery':
        this.addDeliveryFields(row, product);
        break;
      case 'consulta':
      case 'tratamento':
        this.addMedicalFields(row, product);
        break;
      case 'aluguel':
        this.addBookingFields(row, product);
        break;
      case 'assinatura':
        this.addSubscriptionFields(row, product);
        break;
    }
    
    return product;
  }

  // Add digital product specific fields
  addDigitalFields(row, product) {
    const fileFormat = this.getFieldValue(row, 'fileFormat', 'fileformat');
    if (fileFormat) product.fileFormat = fileFormat;
    
    const licenseType = this.getFieldValue(row, 'licenseType', 'licensetype');
    if (licenseType) product.licenseType = licenseType;
    
    const contextUrl = this.getFieldValue(row, 'contextUrl', 'contexturl');
    if (contextUrl) product.contextUrl = contextUrl;
  }

  // Add physical product specific fields
  addPhysicalFields(row, product) {
    // Handle lowercased header from Papa.parse transformHeader
    const rawWeight = this.getFieldValue(row, 'weightKg', 'weightkg', 'weight_kg');
    if (rawWeight) {
      product.weightKg = parseFloat(rawWeight) || 0;
    }
    
    if (row.height_cm || row.width_cm || row.depth_cm) {
      product.dimensionsCm = [
        parseFloat(row.height_cm) || 0,
        parseFloat(row.width_cm) || 0,
        parseFloat(row.depth_cm) || 0
      ];
    }
    
    if (row.color) product.color = row.color;
    
    const shipsTo = this.getFieldValue(row, 'shipsTo', 'shipsto');
    if (shipsTo) product.shipsTo = shipsTo;
    
    const contextUrl = this.getFieldValue(row, 'contextUrl', 'contexturl');
    if (contextUrl) product.contextUrl = contextUrl;
  }

  // Add delivery service specific fields
  addDeliveryFields(row, product) {
    const maxDistance = this.getFieldValue(row, 'maxDistance', 'maxdistance');
    if (maxDistance) product.maxDistance = maxDistance;
    
    // Check both camelCase and lowercase versions
    const fixedPriceValue = this.getFieldValue(row, 'isFixedPrice', 'isfixedprice');
    if (fixedPriceValue) {
      product.isFixedPrice = this.normalizeBooleanValue(fixedPriceValue);
    }
    
    const estimatedValue = this.getFieldValue(row, 'estimatedMinutes', 'estimatedminutes');
    if (estimatedValue) product.estimatedMinutes = parseInt(estimatedValue) || 0;
    
    if (row.address) product.address = row.address;
    if (row.link) product.contextUrl = row.link;
  }

  // Add medical service specific fields (consulta/tratamento)
  addMedicalFields(row, product) {
    this.addBookingFields(row, product);
    
    if (row.specialty) product.specialty = row.specialty;
    
    const durationValue = this.getFieldValue(row, 'durationMinutes', 'durationminutes');
    if (durationValue) product.durationMinutes = parseInt(durationValue) || 0;
  }

  // Add subscription specific fields
  addSubscriptionFields(row, product) {
    const planType = this.getFieldValue(row, 'planType', 'plantype');
    if (planType) product.planType = planType;
    
    if (row.address) product.address = row.address;
    if (row.link) product.contextUrl = row.link;
  }

  // Process XLSX data rows
  processXlsxData(jsonData) {
    if (jsonData.length === 0) {
      throw new Error('Planilha está vazia');
    }
    
    // First row is headers, rest are data
    const headers = jsonData[0].map(header => (header || '').toString().trim().toLowerCase());
    const dataRows = jsonData.slice(1);
    
    // Convert to object format (array of objects)
    const parsedData = dataRows
      .filter(row => row.some(cell => cell && cell.toString().trim())) // Filter empty rows
      .map(row => {
        const rowObj = {};
        headers.forEach((header, index) => {
          rowObj[header] = this.normalizeCellValue(row[index]);
        });
        return rowObj;
      });

    return { headers, parsedData };
  }
}

// Export as singleton
export default new SpreadsheetProcessorService();